import {html, TemplateResult} from "lit";

import {Article, Messages} from "../../../@types/online-reservation.js";
import '../product-section/product-section.js';
import '../reservation-badges/reservation-badges.js';
import checkIcon from '@breuni-ui/bdk/dist/design-tokens/images/icons/o-ic-24_check.svg';
import {addPresentationToIcon} from "../../icon/icon-accessibility.js";
import {Step } from "./step.js";

const DEFAULT_ARTICLE: Article = {
  brand: 'Test Brand',
  name: 'Test Article Name',
  color: 'Black',
  price: {
    value: '29.99 €',
    priceReduced: false
  },
  image: {
    src: 'https://cms.brnstc.de/product_images/435x596p/cpro/media/images/product/24/7/100159661215000_0_1722163931092.webp',
    alt: 'Test Article Image'
  }
};

interface ORResultOptions {
  article?: Article;
  sizeDescription?: string;
  storeLocationName?: string;
  showSizeBadge?: boolean;
  reservationSuccessful?: boolean;
}

export class ORResult extends Step {
  private readonly reservationSuccessful: boolean;
  private readonly article: Article;
  private readonly sizeDescription: string;
  private readonly storeLocationName: string;
  private readonly showSizeBadge: boolean;

  constructor(
    private readonly messages: Messages,
    private readonly onNext: () => void,
    private readonly onRetry: () => void,
    options: ORResultOptions = {}
  ) {
    super();
    this.article = options.article ?? DEFAULT_ARTICLE;
    this.sizeDescription = options.sizeDescription ?? 'S - 32';
    this.storeLocationName = options.storeLocationName ?? 'Stuttgart';
    this.showSizeBadge = options.showSizeBadge ?? true;
    this.reservationSuccessful = options.reservationSuccessful ?? true;
  }

  protected renderContent(): TemplateResult {
    return html`
      <div class="ents-reservation-closed">
        <div class="ents-reservation-closed__product_section">
          <product-section .article=${this.article}></product-section>
        </div>
        <reservation-badges
          .sizeDescription=${this.sizeDescription}
          .storeLocationName=${this.storeLocationName}
          .showSizeBadge=${this.showSizeBadge}
        ></reservation-badges>
        ${this.reservationSuccessful ? this.successContent() : this.failureContent()}
      </div>
    `;
  }

  protected renderFooter(): TemplateResult {
    return this.reservationSuccessful ? this.successFooter() : this.failureFooter();
  }

  protected renderTitle(): TemplateResult {
    return html`
      <div id="ents-or-result"></div>
      ${this.reservationSuccessful ? this.successTitle() : this.failureTitle()}
    `;
  }

  private renderButton(
    label: string,
    variant: 'primary' | 'outline',
    clickHandler: () => void,
    extraClasses = ''
  ) {
    return html`
      <button
        class="bdk-button ${extraClasses}"
        type="button"
        variant=${variant}
        size="medium"
        full-width
        @click=${clickHandler}
      >
        ${label}
      </button>
    `;
  }

  private successTitle() {
    return html `
      <div class="ents-reservation-closed__header">
        ${addPresentationToIcon(checkIcon())}
        <h2 class="ents-heading-medium ents-reservation-closed__header--text">${this.messages.success}</h2>
      </div>
    `;
  }

  private failureTitle() {
    return html `
      <div class="ents-reservation-closed__header">
        <h2 class="ents-heading-medium error">${this.messages.errorHeadline}</h2>
      </div>
    `;
  }

  private successContent() {
    return html `
      <span class="ents-copy">${this.messages.successText}</span>
    `;
  }

  private failureContent() {
    return html `
      <p class="ents-copy">${this.messages.error}</p>
    `;
  }

  private successFooter() {
    return html`
      <div class="ents-reservation-closed__button">
        ${this.renderButton(this.messages.close, 'primary', this.onNext)}
      </div>
    `;
  }

  private failureFooter() {
    return html`
      <div class="ents-reservation-closed__button">
        ${this.renderButton(this.messages.close, 'outline', this.onNext)}
        ${this.renderButton(this.messages.tryAgain, 'primary', this.onRetry)}
      </div>
    `;
  }
}
